import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Grid, Tab, Tabs} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Link, useParams, useSearchParams} from 'react-router';
import {JobSummary} from '../../../../component/details/jobSummary';
import {AdminShippingManagement} from './Shipping/shippingManagement';
import {Processing} from './Processing/Processing';
import {OrderDocuments} from './JobDocuments/jobDocuments';
import {fetchCustomer, fetchJob, fetchShipmentDetails, patchJob} from "../../../../utils/apiCalls";
import {fetchCompanyList} from "../../../../utils/dashboardCalls";
import {ConfirmDialog, StyledButtonContained} from "../../../../utils/uiUtils";
import {ORDER_SHIPPING_STATUSES, ORDER_PROCESSING_STATUSES} from "../../../../utils/constants";
import {PROCESSING_TAB, TAB_PARAM} from "../../../../component/widgets/MediaMetrics/mediaFilterNavigation";





/*
 * The tabs, by position, because that is how MUI's Tabs identifies them and how TabPanel is indexed.
 *
 * Named because two things now choose a tab without a click — the order's status and the `?tab=` parameter —
 * and a bare 2 in either of them is a number nobody can check against the list below.
 */
const JOB_TAB = 0;
const SHIPPING_TAB_INDEX = 1;
const PROCESSING_TAB_INDEX = 2;

function TabPanel(props) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tabpanel-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{p: 3}}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const CustomTabList = styled(Tabs)(() => ({
    borderRight: 0,
    maxWidth: '100% !important',
    minHeight: '100vh',
    height: '100%',
    background: '#FFFFFF1A',
    color: '#FFFFFF',
    '& .MuiTabs-flexContainer': {width: '100%'},
    '& .MuiTabs-scroller': {background: 'rgba(255,255,255,0.1)'},
    '& .MuiTabs-indicator': {background: '#FFC540'},
    '& .MuiTab-root': {color: '#FFFFFF'},
    textTransform: 'none',
}));

const CustomTab = styled(Tab)(() => ({
    textTransform: 'none',
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: '100%',
    height: '40px',
    fontFamily: 'Poppins',
    color: '#FFFFFF',
    '&.Mui-selected': {
        background: '#FFC540',
        color: '#3A3E3F !important',
        height: '40px',
    },
    '&.Mui-disabled': {
        opacity: 0.5,
        pointerEvents: 'none',
    },
}));

export const AdminJobManagement = () => {


    const [searchParams] = useSearchParams();

    /*
     * The tab asked for by the URL, or null when the URL does not care.
     *
     * Put there by clicking a Media Metrics tile, which links here with `?tab=processing&media=<key>` so the
     * tape table it wants filtered is the table on screen. Read on every render rather than only on mount:
     * following a second such link from this very page changes the query string without remounting.
     *
     * An unrecognised value is null, not an error — a mistyped or outdated link should open the job the way
     * it always did.
     */
    const requestedTab = searchParams.get(TAB_PARAM) === PROCESSING_TAB ? PROCESSING_TAB_INDEX : null;

    // Seeded from the URL so an arriving link opens on its tab directly, rather than showing the Job tab for
    // one paint and mounting Processing — and its dozen count queries — a moment later.
    const [activeTab, setActiveTab] = useState(requestedTab ?? JOB_TAB);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const params = useParams();
    const orderId = params.orderId;

    const [orderDetail, setOrderDetail] = useState({});

    const statusToTab = (status) => {
        if (ORDER_SHIPPING_STATUSES.includes(status)) return SHIPPING_TAB_INDEX;
        if (ORDER_PROCESSING_STATUSES.includes(status)) return PROCESSING_TAB_INDEX;
        return JOB_TAB;
    };

    /*
     * The tab the order's own status implies — unless the URL named one, which wins.
     *
     * The status guess is right for someone opening a job to work on it: an order out for shipping opens on
     * Shipping. It is wrong for someone who followed a link to a specific table, and it would win by
     * default, because it lands after the first paint when the order detail arrives. A tile clicked on a job
     * still marked as shipping would flash the filtered table and then be dragged to the shipping tab.
     */
    useEffect(() => {
        if (requestedTab !== null) {
            setActiveTab(requestedTab);
            return;
        }
        if (orderDetail?.status) {
            setActiveTab(statusToTab(orderDetail.status));
        }
        // statusToTab is a pure function of the module's status constants.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderDetail?.status, requestedTab]);
    const [customer, setCustomer] = useState(null);
    const [shipments, setShipments] = useState(null);
    const [companyCode, setCompanyCode] = useState(null);
    const [startProcessingDialogOpen, setStartProcessingDialogOpen] = useState(false);

    const canStartProcessing = orderDetail?.status === 'draft' || ORDER_SHIPPING_STATUSES.includes(orderDetail?.status);

    const handleStartProcessing = () => {
        patchJob(orderId, orderDetail.customer, {status: 'audit_started'})
            .then(data => {
                if (data) {
                    setOrderDetail(data);
                    setStartProcessingDialogOpen(false);
                }
            })
            .catch(err => console.log(err));
    };

    const getCustomerDetail = (order) => {
        let customerData;
        fetchCustomer((order ? order : orderDetail)?.customer)
            .then(data => {
                customerData = data;
                setCustomer(data);
                return fetchCompanyList();
            })
            .then(companyData => {
                if (companyData?.result?.data && customerData?.arkbridge_customer_id) {
                    const arkId = parseInt(customerData.arkbridge_customer_id, 10);
                    const company = companyData.result.data.find(c => c.company_id === arkId);
                    if (company) setCompanyCode(company.code);
                }
            })
            .catch(err => console.log(err));
    };

    const getShipmentDetails = (order) => {
        fetchShipmentDetails((order ? order : orderDetail)?.uuid)
            .then(data => setShipments(data.results))
            .catch(err => console.log(err));
    };

    useEffect(() => {
        getOrderDetail();
    }, []);

    const getOrderDetail = () => {
        fetchJob(params.orderId)
            .then(data => {
                setOrderDetail(data);
                if (customer === null) {
                    getCustomerDetail(data);
                }
                if (shipments === null) {
                    getShipmentDetails(data);
                }
            })
            .catch(err => console.log(err));
    };

    return (
            <Grid container direction='column' px={4}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <img src="/assets/img/cart.svg" alt=""/>
                        <img src="/assets/img/arrow_left.svg" alt=""/>
                        <Link to='/Jobs' style={{textDecoration: 'none'}}> <small className="grey-color">Your Jobs</small></Link>
                        <img src="/assets/img/arrow_left.svg" alt=""/>
                        <small className="grey-color">{orderId}</small>
                    </Box>
                    {canStartProcessing && (
                        <StyledButtonContained
                            variant='contained'
                            onClick={() => setStartProcessingDialogOpen(true)}
                            sx={{height: '40px'}}
                        >
                            Start Processing
                        </StyledButtonContained>
                    )}
                </Box>
                <ConfirmDialog
                    open={startProcessingDialogOpen}
                    onClose={() => setStartProcessingDialogOpen(false)}
                    onConfirm={handleStartProcessing}
                    title="Start Processing"
                    message="This will move the job to the Processing stage. Shipping can still be managed for additional shipments. Continue?"
                />


                <Grid item xs={12} sx={{minHeight: 'max-content', border: '1px solid #D8D8D8', borderRadius: '5px', mb: '100px'}}>
                    <Grid container>
                        <Grid item xs={2}>
                            <CustomTabList
                                orientation='vertical'
                                value={activeTab}
                                onChange={handleTabChange}
                                className='vertical-pane'>
                                <CustomTab className='vertical-pane' label="Job" {...a11yProps(0)} style={{width: '100%', maxWidth: '100%'}}/>
                                <CustomTab disabled={orderDetail?.status === 'draft'} className='vertical-pane' label="Shipping" {...a11yProps(1)} style={{width: '100%', maxWidth: '100%'}}/>
                                <CustomTab disabled={orderDetail?.status === 'draft'} className='vertical-pane' label="Processing" {...a11yProps(2)} style={{width: '100%', maxWidth: '100%'}}/>
                                <CustomTab disabled={orderDetail?.status === 'draft'} className='vertical-pane' label="Documents" {...a11yProps(3)} style={{width: '100%', maxWidth: '100%'}}/>
                            </CustomTabList>
                        </Grid>

                        <Grid item xs={10} sx={{background: '#3A3E3F', color: '#FFFFFF', padding: 2}}>
                            <TabPanel value={activeTab} index={0}>
                                {(orderDetail && customer) && (
                                    <JobSummary orderDetail={orderDetail} customer={customer} companyCode={companyCode} displayBilling={true}/>
                                )}
                            </TabPanel>
                            <TabPanel value={activeTab} index={1}>
                                {(shipments) && (
                                    <AdminShippingManagement
                                        shipments={shipments}
                                        customer={customer}
                                        refreshShipmentDetails={() => {
                                            getShipmentDetails();
                                        }}
                                        order={orderDetail}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={activeTab} index={2}>
                                {orderDetail && <Processing orderDetail={orderDetail} customer={customer} onOrderUpdate={setOrderDetail}/>}
                            </TabPanel>
                            <TabPanel value={activeTab} index={3}>
                                {orderDetail && <OrderDocuments orderDetail={orderDetail} canDelete={true} showHeading={true} enableUpload={true}/>}
                            </TabPanel>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
    );
};
